import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getErrorHttpMessage } from "utils";
import { StoreType } from "types/genericTypes";
import { useIntl } from "react-intl";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  activarTipoProductoHttp,
  createTipoProductoHttp,
  desactivarTipoProductoHttp,
  editarTipoProductoHttp,
  getTipoProductosHttp,
} from "actions/tipoProducto";
import {
  activarUnidadMedidaHttp,
  createUnidadMedidaHttp,
  desactivarUnidadMedidaHttp,
  editarUnidadMedidaHttp,
  getUnidadesMedidaHttp,
} from "actions/unidadMedida";

export const useUnidadMedida = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [procesando, setProcesando] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [mensajeAlert, setMensajeAlert] = useState("");
  const [errorLogin, setErrorLogin] = useState(false);
  const handleisAlertOpen = () => setIsAlertOpen(true);
  const handleisAlertClose = () => setIsAlertOpen(false);

  /* CREAR CATEGORÍA */
  const [procesandoUnidadMedida, setProcesandoUnidadMedida] = useState<boolean>(false);
  const [isAlertOpenCrearUnidadMedida, setIsAlertOpenCrearUnidadMedida] = useState(false);
  const handleisAlertCrearUnidadMedidaOpen = () => setIsAlertOpenCrearUnidadMedida(true);
  const handleisAlertCrearUnidadMedidaClose = () => setIsAlertOpenCrearUnidadMedida(false);

  const [verEditarUnidadMedida, setVerEditarUnidadMedida] = useState<any>(null);
  const [isAlertOpenEditarUnidadMedida, setIsAlertOpenEditarUnidadMedida] = useState(false);
  const handleisAlertOpenEditarUnidadMedida = () => setIsAlertOpenEditarUnidadMedida(true);
  const handleisAlertCloseEditarUnidadMedida = () => setIsAlertOpenEditarUnidadMedida(false);

  const [unidadesMedida, setUnidadesMedida] = useState<any[]>([]);

  // Estados para la paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  //para buscador
  const [buscador, setBuscador] = useState("");
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  //Debounce para evitar muchas peticiones
  const handleBuscadorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBuscador(value);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    const timeout = setTimeout(() => {
      getUnidadMedida({ search: value });
    }, 500);
    setDebounceTimeout(timeout);
  };

  const formik = useFormik({
    initialValues: {
      clave: "",
      nombre: "",
      descripcion: "",
    },
    validationSchema: Yup.object({
      clave: Yup.string(),
      nombre: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
      descripcion: Yup.string().required(intl.formatMessage({ id: "input_validation_requerido" })),
    }),
    onSubmit: async (values) => {
      //console.log("Formulario enviado:", values);
    },
  });

  const isFieldValid = (fieldName: keyof typeof formik.values) => {
    return (
      formik.touched[fieldName] &&
      !formik.errors[fieldName] &&
      formik.values[fieldName] &&
      formik.values[fieldName] !== ""
    );
  };

  const getFieldColor = (fieldName: keyof typeof formik.values) => {
    return isFieldValid(fieldName) ? "#00AB16" : undefined;
  };

  const getUnidadMedida = useCallback(
    async (params?: { search?: string; fecha1?: string; fecha2?: string }) => {
      try {
        setProcesando(true);
        const unidadesMedida = await getUnidadesMedidaHttp(params?.search);
        setUnidadesMedida(unidadesMedida);
        setProcesando(false);
      } catch (error) {
        setProcesando(false);
        const message = getErrorHttpMessage(error);
        setMensajeAlert(message || intl.formatMessage({ id: "get_elementos_error" }));
        handleisAlertOpen();
      }
    },
    []
  );

  const createUnidadMedida = async (datos: any) => {
    try {
      setProcesandoUnidadMedida(true);
      const response: any = await createUnidadMedidaHttp(datos);
      setUnidadesMedida((prevUnidadMedidas: any[]) => [
        { ...response, productos: 0 },
        ...prevUnidadMedidas,
      ]);

      setMensajeAlert(intl.formatMessage({ id: "general_añadido_correctamente" }));
      formik.resetForm();
      handleisAlertCrearUnidadMedidaClose();
      handleisAlertOpen();
      setProcesandoUnidadMedida(false);
    } catch (error) {
      setProcesandoUnidadMedida(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "error_añadir_general" }));
      handleisAlertOpen();
    }
  };
  const editarUnidadMedida = async (datos: any) => {
    try {
      setProcesandoUnidadMedida(true);
      const response: any = await editarUnidadMedidaHttp(datos);

      setUnidadesMedida((prevUnidadMedidas: any[]) =>
        prevUnidadMedidas.map((c) =>
          c.id === response?.id
            ? {
                ...c,
                clave: response?.clave,
                nombre: response?.nombre,
                descripcion: response?.descripcion,
              }
            : c
        )
      );
      setMensajeAlert(intl.formatMessage({ id: "general_editado_correctamente" }));
      formik.resetForm();
      handleisAlertCloseEditarUnidadMedida();
      handleisAlertOpen();
      setProcesandoUnidadMedida(false);
    } catch (error) {
      setProcesandoUnidadMedida(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_editado_error" }));
      handleisAlertOpen();
    }
  };

  const desactivarUnidadMedida = async (datos: any) => {
    try {
      setProcesando(true);
      const UnidadMedida: any = await desactivarUnidadMedidaHttp(datos);
      setUnidadesMedida((prevTipoProductos: any[]) => {
        return prevTipoProductos.map((c) => {
          if (c.id === UnidadMedida.id) {
            return {
              ...c,
              estatus: UnidadMedida.estatus,
            };
          }
          return c;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "general_desactivado_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_desactivado_error" }));
      handleisAlertOpen();
    }
  };
  const activarUnidadMedida = async (datos: any) => {
    try {
      setProcesando(true);
      const UnidadMedida: any = await activarUnidadMedidaHttp(datos);
      setUnidadesMedida((prevUnidadMedidas: any[]) => {
        return prevUnidadMedidas.map((t) => {
          if (t.id === UnidadMedida.id) {
            return {
              ...t,
              estatus: UnidadMedida.estatus,
            };
          }
          return t;
        });
      });
      setMensajeAlert(intl.formatMessage({ id: "general_activado_correctamente" }));
      handleisAlertOpen();
      setProcesando(false);
    } catch (error) {
      setProcesando(false);
      const message = getErrorHttpMessage(error);
      setMensajeAlert(message || intl.formatMessage({ id: "general_activado_error" }));
      handleisAlertOpen();
    }
  };

  useEffect(() => {
    if (verEditarUnidadMedida) {
      formik.setValues({
        clave: verEditarUnidadMedida?.clave || "",
        nombre: verEditarUnidadMedida?.nombre || "",
        descripcion: verEditarUnidadMedida?.descripcion || "",
      });
    }
  }, [verEditarUnidadMedida]);

  useEffect(() => {
    getUnidadMedida();
  }, []);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const unidadesMedidaPaginadas = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return unidadesMedida?.slice(startIndex, endIndex) || [];
  }, [unidadesMedida, page, rowsPerPage]);

  return {
    intl,
    formik,
    procesando,
    isAlertOpen,
    mensajeAlert,
    handleisAlertClose,
    unidadesMedida,
    //crear categoria
    isAlertOpenCrearUnidadMedida,
    handleisAlertCrearUnidadMedidaOpen,
    handleisAlertCrearUnidadMedidaClose,
    getFieldColor,
    procesandoUnidadMedida,
    createUnidadMedida,
    desactivarUnidadMedida,
    activarUnidadMedida,
    editarUnidadMedida,
    verEditarUnidadMedida,
    isAlertOpenEditarUnidadMedida,
    handleisAlertOpenEditarUnidadMedida,
    handleisAlertCloseEditarUnidadMedida,
    setVerEditarUnidadMedida,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    buscador,
    handleBuscadorChange,
    unidadesMedidaPaginadas,
  };
};
